import { Component, OnInit, AfterViewInit, ChangeDetectorRef, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { TransportPickupService } from 'src/app/services/transport-pickup/transport-pickup.service';
import { TransportPickupModalService } from 'src/app/services/transport-pickup-modal/transport-pickup-modal.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

// Define filter interface
interface TransportPickupFilters {
  keyword?: string;
  stock_number?: string;
  order_date_from?: string;
  order_date_to?: string;
  cut_date_from?: string;
  cut_date_to?: string;
  yard_in_date_from?: string;
  yard_in_date_to?: string;
  yard_out_date_from?: string;
  yard_out_date_to?: string;
}

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-transport-pickup-listing',
  templateUrl: './transport-pickup-listing.component.html',
  styleUrls: ['./transport-pickup-listing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class TransportPickupListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() transportPickupAdded = new EventEmitter<void>();
  @Output() transportPickupUpdated = new EventEmitter<void>();

  // Data properties
  transportPickups: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFilters: boolean = false;

  // Sorting properties
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Filter properties
  filters: TransportPickupFilters = {};

  // Selection properties
  selectedTransportPickups: Set<string> = new Set();
  selectAll: boolean = false;

  // Send mail loader
  isSendingMail: boolean = false;

  constructor(
    private _transportPickupService: TransportPickupService,
    private _transportPickupModalService: TransportPickupModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.GetData();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    // Initialize menus after view is initialized
    this.initializeMenus();
  }

  private setupModalSubscriptions(): void {
    // No specific modal subscriptions needed for this component
    // The modal events are handled by the parent component
  }

  GetData(page: number = this.currentPage, search: string = this.searchTerm, filters: TransportPickupFilters = this.filters): void {
    this.isLoading = true;
    this.currentPage = page;

    // Build filters object
    const apiFilters: any = {};

    if (filters.keyword) {
      apiFilters.keyword = filters.keyword;
    }
    if (filters.stock_number) {
      apiFilters.stock_number = filters.stock_number;
    }
    if (filters.order_date_from) {
      apiFilters.order_date_from = filters.order_date_from;
    }
    if (filters.order_date_to) {
      apiFilters.order_date_to = filters.order_date_to;
    }
    if (filters.cut_date_from) {
      apiFilters.cut_date_from = filters.cut_date_from;
    }
    if (filters.cut_date_to) {
      apiFilters.cut_date_to = filters.cut_date_to;
    }
    if (filters.yard_in_date_from) {
      apiFilters.yard_in_date_from = filters.yard_in_date_from;
    }
    if (filters.yard_in_date_to) {
      apiFilters.yard_in_date_to = filters.yard_in_date_to;
    }
    if (filters.yard_out_date_from) {
      apiFilters.yard_out_date_from = filters.yard_out_date_from;
    }
    if (filters.yard_out_date_to) {
      apiFilters.yard_out_date_to = filters.yard_out_date_to;
    }

    // Add sorting parameters
    if (this.sortBy) {
      apiFilters.sort_by_column = this.sortBy;
      apiFilters.sort_order = this.sortOrder;
    }

    this.subscriptions.push(
      this._transportPickupService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          console.log('API Response:', response); // Debug log
          if (response.status && response.data) {
            this.transportPickups = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            console.log('Transport pickups set:', this.transportPickups); // Debug log
            console.log('Pagination info:', this.paginationInfo); // Debug log
          } else {
            this.transportPickups = [];
            this.resetPagination();
            console.log('No data found in response'); // Debug log
          }
          this.isLoading = false;
          this.cdr.detectChanges();

          // Initialize menus after data loads
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading transport pickups:', error);
          this.transportPickups = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.GetData(page, this.searchTerm, this.filters);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.GetData();
  }

  resetFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    this.showFilters = false;
    this.currentPage = 1;
    this.GetData();
  }

  openAddTransportPickupModal(): void {
    this._transportPickupModalService.clearSelectedTransportPickup();
    const modalElement = document.getElementById('kt_modal_transport_pickup_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openBulkUpdateModal(): void {
    if (this.selectedTransportPickups.size === 0) {
      return;
    }
    const selectedIds = Array.from(this.selectedTransportPickups);
    const selectedRecords = this.transportPickups.filter(tp =>
      selectedIds.includes(tp._id || tp.id)
    );
    this._transportPickupModalService.setBulkItems(selectedRecords);
    const modalElement = document.getElementById('kt_modal_transport_pickup_bulk_update');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onBulkUpdated(): void {
    this.GetData(this.currentPage, this.searchTerm, this.filters);
    this.selectedTransportPickups.clear();
    this.selectAll = false;
  }

  openEditTransportPickupModal(transportPickup: any): void {
    this._transportPickupModalService.setSelectedTransportPickup(transportPickup);
    const modalElement = document.getElementById('kt_modal_transport_pickup_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteTransportPickup(transportPickup: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete this transport pickup?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(transportPickup);
      }
    });
  }

  private performDelete(transportPickup: any): void {
    const transportPickupId = transportPickup._id || transportPickup.id;

    this.subscriptions.push(
      this._transportPickupService.DeleteDataById(transportPickupId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Transport pickup has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Reload the current page
              this.GetData(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete transport pickup.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error deleting transport pickup:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete transport pickup. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onTransportPickupAdded(): void {
    console.log('Transport pickup added, refreshing list...');
    this.GetData(this.currentPage, this.searchTerm, this.filters);
    this.transportPickupAdded.emit();
  }

  onTransportPickupUpdated(): void {
    console.log('Transport pickup updated, refreshing list...');
    this.GetData(this.currentPage, this.searchTerm, this.filters);
    this.transportPickupUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  // Sorting methods
  onSort(column: string): void {
    if (this.sortBy === column) {
      // Toggle sort order if same column
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new column and default to ascending
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    // Reset to page 1 and reload with new sort
    this.GetData(1, this.searchTerm, this.filters);
  }

  getSortClass(column: string): string {
    if (this.sortBy !== column) {
      return 'sort-neutral';
    }
    return this.sortOrder === 'asc' ? 'sort-asc' : 'sort-desc';
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  // Selection methods
  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    if (this.selectAll) {
      this.transportPickups.forEach(transportPickup => {
        this.selectedTransportPickups.add(transportPickup._id || transportPickup.id);
      });
    } else {
      this.selectedTransportPickups.clear();
    }
  }

  toggleTransportPickupSelection(transportPickupId: string): void {
    if (this.selectedTransportPickups.has(transportPickupId)) {
      this.selectedTransportPickups.delete(transportPickupId);
    } else {
      this.selectedTransportPickups.add(transportPickupId);
    }

    // Update select all checkbox state
    this.selectAll = this.selectedTransportPickups.size === this.transportPickups.length;
  }

  isTransportPickupSelected(transportPickupId: string): boolean {
    return this.selectedTransportPickups.has(transportPickupId);
  }

  get hasSelectedTransportPickups(): boolean {
    return this.selectedTransportPickups.size > 0;
  }

  // Send mail methods
  sendSingleMail(transportPickup: any): void {
    const transportPickupId = transportPickup._id || transportPickup.id;
    this.sendMail([transportPickupId]);
  }

  sendBulkMail(): void {
    if (this.selectedTransportPickups.size === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one transport pickup to send mail.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Check if all selected records have the same transport_company_id
    const selectedIds = Array.from(this.selectedTransportPickups);
    const selectedRecords = this.transportPickups.filter(tp =>
      selectedIds.includes(tp._id || tp.id)
    );

    // Get unique transport_company_ids from selected records
    const transportCompanyIds = new Set(
      selectedRecords.map(tp => tp.transport_company_id).filter(id => id !== null && id !== undefined)
    );

    // Check if all selected records have the same transport_company_id
    if (transportCompanyIds.size > 1) {
      Swal.fire({
        title: 'Invalid Selection',
        text: 'All selected transport pickups must have the same transport company.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Check if any selected record has no transport_company_id
    const hasEmptyTransportCompany = selectedRecords.some(tp =>
      !tp.transport_company_id
    );

    if (hasEmptyTransportCompany && transportCompanyIds.size > 0) {
      Swal.fire({
        title: 'Invalid Selection',
        text: 'All selected transport pickups must have the same transport company.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.sendMail(selectedIds);
  }

  private sendMail(transportPickupIds: string[]): void {
    const data = {
      transport_pickup_ids: transportPickupIds
    };

    // Set loading state
    this.isSendingMail = true;

    this.subscriptions.push(
      this._transportPickupService.SendMail(data).subscribe({
        next: (response: any) => {
          // Reset loading state
          this.isSendingMail = false;

          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Mail sent successfully for ${transportPickupIds.length} transport pickup(s).`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Clear selections after successful send
              this.selectedTransportPickups.clear();
              this.selectAll = false;
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to send mail.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          // Reset loading state
          this.isSendingMail = false;

          console.error('Error sending mail:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while sending mail.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }
}
